import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "../../ui/UIElements/Icon";
import Pagination from "../../ui/Data/Pagination";
import CampaignsLoading from "../../ui/Campaigns/CampaignsLoading";
import CampaignsError from "../../ui/Campaigns/CampaignsError";
import EmailConfigurationForm from "../../ui/Campaigns/EmailConfigurationForm";
import { useCampaigns } from "../../../hooks/useCampaigns";
import { useIsSmtpConfigured } from "../../../hooks/useEmailConfig";
import { useUrlStates } from "@finitless/shared/hooks";
import {
  CAMPAIGN_STATUS_OPTIONS,
  CAMPAIGNS_PER_PAGE,
  MAX_VISIBLE_PAGES,
  getContactTypeBadge,
  hasDetailedStats,
  getTimeAgo,
} from "../../../constants/campaignConstants";

const CampaignsPage = () => {
  const { t } = useTranslation("campaigns");
  const navigate = useNavigate();
  const { isConfigured: isSmtpConfigured, isLoading: isSmtpLoading } =
    useIsSmtpConfigured();
  const [showConfigForm, setShowConfigForm] = useState(false);

  const initialUrlStates = useMemo(
    () => ({
      contactType: "all",
      status: "all",
      searchTerm: "",
      currentPage: "1",
    }),
    [],
  );

  const [urlStates, updateUrlState] = useUrlStates(initialUrlStates);

  const [searchInput, setSearchInput] = useState(urlStates.searchTerm || "");
  const [contactType] = useState(urlStates.contactType || "all");
  const [statusFilter, setStatusFilter] = useState(urlStates.status || "all");
  const [searchTerm, setSearchTerm] = useState(urlStates.searchTerm || "");
  const [currentPage, setCurrentPage] = useState(
    parseInt(urlStates.currentPage) || 1,
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      updateUrlState("searchTerm", searchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput, updateUrlState]);

  const { data, isLoading, error, refetch } = useCampaigns({
    contactType,
    status: statusFilter,
    searchTerm,
    page: currentPage,
    pageSize: CAMPAIGNS_PER_PAGE,
  });

  const campaigns = data?.campaigns || [];
  const pagination = data?.pagination || {};

  const calculatedTotalPages = pagination?.total
    ? Math.ceil(pagination.total / CAMPAIGNS_PER_PAGE)
    : 1;

  const totalPages = pagination?.total_pages || calculatedTotalPages;

  const handleStatusFilterChange = (newStatus) => {
    setStatusFilter(newStatus);
    setCurrentPage(1);
    updateUrlState("status", newStatus);
    updateUrlState("currentPage", "1");
  };

  const handleSearchChange = (term) => {
    setSearchInput(term);
    setCurrentPage(1);
    updateUrlState("currentPage", "1");
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setCurrentPage(1);
    updateUrlState("searchTerm", "");
    updateUrlState("currentPage", "1");
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    updateUrlState("currentPage", page.toString());
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleViewDetails = (campaign) => {
    navigate(`/campaigns/${campaign.id}`, { state: { campaign } });
  };

  const handleCreateCampaign = () => {
    navigate("/campaigns/new");
  };

  const totalSent = campaigns.reduce((acc, c) => acc + (c.sent_count || 0), 0);
  const totalOpens = campaigns.reduce((acc, c) => acc + (c.open_count || 0), 0);
  const totalClicks = campaigns.reduce(
    (acc, c) => acc + (c.clicked_count || 0),
    0,
  );
  const avgOpenRate =
    totalSent > 0 ? ((totalOpens / totalSent) * 100).toFixed(1) : "0.0";
  const avgClickRate =
    totalSent > 0 ? ((totalClicks / totalSent) * 100).toFixed(1) : "0.0";

  const getStatusOptionLabel = (option) => {
    const labelByValue = {
      all: t("filters.status.all"),
      draft: t("filters.status.draft"),
      ended: t("filters.status.sent"),
      scheduled: t("filters.status.scheduled"),
      pending: t("filters.status.active"),
    };

    return labelByValue[option.value] || option.label;
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      ended: t("status.sent"),
      pending: t("status.active"),
      scheduled: t("status.scheduled"),
      draft: t("status.draft"),
    };

    if (!status) {
      return t("status.unknown");
    }

    return (
      statusLabels[status] || status.charAt(0).toUpperCase() + status.slice(1)
    );
  };

  if (isSmtpLoading) {
    return <CampaignsLoading />;
  }

  if (!isSmtpConfigured || showConfigForm) {
    return (
      <EmailConfigurationForm
        onConfigured={() => setShowConfigForm(false)}
        onCancel={isSmtpConfigured ? () => setShowConfigForm(false) : null}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <div>
          <h1 className="text-2xl font-bold text-white">{t("header.title")}</h1>
          <p className="mt-1 text-gray-400">{t("header.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowConfigForm(true)}
            className="flex items-center space-x-2 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-300 transition-colors hover:bg-gray-700"
            title={t("actions.emailSettings")}
          >
            <Icon name="Settings" className="h-4 w-4" />
            <span className="hidden sm:inline">
              {t("actions.emailSettings")}
            </span>
          </button>
          <button
            onClick={handleCreateCampaign}
            className="flex items-center space-x-2 rounded-lg bg-blue-600 px-3 py-2 text-sm text-white transition-colors hover:bg-blue-700 sm:px-4"
          >
            <Icon name="Plus" className="h-4 w-4" />
            <span>{t("actions.newCampaign")}</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Total Sent */}
        <div className="flex items-center justify-between rounded-xl border border-gray-800 bg-gray-900 p-5">
          <div>
            <p className="mb-1 text-xs tracking-wider text-gray-400 uppercase">
              {t("stats.totalSent")}
            </p>
            <div className="flex items-baseline space-x-2">
              <span className="text-2xl font-bold text-white">
                {totalSent >= 1000
                  ? `${(totalSent / 1000).toFixed(1)}k`
                  : totalSent}
              </span>
            </div>
          </div>
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-500/20">
            <Icon name="Send" className="h-5 w-5 text-purple-400" />
          </div>
        </div>

        {/* Avg. Open Rate */}
        <div className="flex items-center justify-between rounded-xl border border-gray-800 bg-gray-900 p-5">
          <div>
            <p className="mb-1 text-xs tracking-wider text-gray-400 uppercase">
              {t("stats.avgOpenRate")}
            </p>
            <div className="flex items-baseline space-x-2">
              <span className="text-2xl font-bold text-white">
                {avgOpenRate}%
              </span>
            </div>
          </div>
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/20">
            <Icon name="Mail" className="h-5 w-5 text-blue-400" />
          </div>
        </div>

        {/* Avg. Click Rate */}
        <div className="flex items-center justify-between rounded-xl border border-gray-800 bg-gray-900 p-5">
          <div>
            <p className="mb-1 text-xs tracking-wider text-gray-400 uppercase">
              {t("stats.avgClickRate")}
            </p>
            <div className="flex items-baseline space-x-2">
              <span className="text-2xl font-bold text-white">
                {avgClickRate}%
              </span>
            </div>
          </div>
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-500/20">
            <Icon name="MousePointerClick" className="h-5 w-5 text-green-400" />
          </div>
        </div>
      </div>

      {/* Search and Filters Bar */}
      <div className="rounded-xl border border-gray-800 bg-gray-900 p-4">
        <div className="flex flex-col gap-3 sm:flex-row">
          {/* Search */}
          <div className="min-w-0 flex-1">
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Icon name="Search" className="h-4 w-4 text-gray-500" />
              </div>
              <input
                type="text"
                placeholder={t("search.placeholder")}
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="h-10 w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 pr-10 pl-10 text-white placeholder-gray-400 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
              {searchInput && (
                <button
                  onClick={handleClearSearch}
                  className="absolute top-1/2 right-2 flex h-5 w-5 -translate-y-1/2 transform items-center justify-center rounded-full bg-gray-600 transition-colors hover:bg-gray-500"
                  type="button"
                >
                  <Icon name="X" className="h-3 w-3 text-gray-300" />
                </button>
              )}
            </div>
          </div>

          {/* Filter Button */}
          <select
            value={statusFilter}
            onChange={(e) => handleStatusFilterChange(e.target.value)}
            className="custom-select h-10 rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
          >
            {CAMPAIGN_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {getStatusOptionLabel(option)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Campaigns Table */}
      {isLoading ? (
        <CampaignsLoading />
      ) : error ? (
        <CampaignsError error={error} onRetry={refetch} />
      ) : campaigns.length > 0 ? (
        <>
          {/* Campaigns Table */}
          <div className="overflow-x-auto rounded-xl border border-gray-800 bg-gray-900">
            <table
              className="w-full min-w-[700px]"
              style={{ tableLayout: "fixed" }}
            >
              <colgroup>
                <col style={{ width: "40%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "30%" }} />
              </colgroup>
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="px-4 py-4 text-left text-xs font-medium tracking-wider text-gray-400 uppercase sm:px-6">
                    {t("table.columns.campaignName")}
                  </th>
                  <th className="px-4 py-4 text-left text-xs font-medium tracking-wider text-gray-400 uppercase sm:px-6">
                    {t("table.columns.type")}
                  </th>
                  <th className="px-4 py-4 text-center text-xs font-medium tracking-wider text-gray-400 uppercase sm:px-6">
                    {t("table.columns.status")}
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-medium tracking-wider whitespace-nowrap text-gray-400 uppercase">
                    {t("table.columns.performance")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/50">
                {campaigns.map((campaign) => {
                  const contactTypeBadge = getContactTypeBadge(
                    campaign.contact_type,
                  );
                  const showStats = hasDetailedStats(campaign.contact_type);
                  const sentCount = campaign.sent_count || 0;
                  const openCount = campaign.open_count || 0;
                  const clickCount = campaign.clicked_count || 0;
                  const openRate =
                    sentCount > 0
                      ? ((openCount / sentCount) * 100).toFixed(1)
                      : "0.0";
                  const clickRate =
                    sentCount > 0
                      ? ((clickCount / sentCount) * 100).toFixed(1)
                      : "0.0";
                  const createdTimeAgo = getTimeAgo(campaign.created_at, {
                    prefix: t("table.createdPrefix"),
                  });

                  return (
                    <tr
                      key={campaign.id}
                      onClick={() => handleViewDetails(campaign)}
                      className="cursor-pointer transition-colors hover:bg-gray-800/50"
                    >
                      {/* Campaign Name */}
                      <td className="overflow-hidden px-4 py-4 sm:px-6">
                        <div
                          className="truncate text-sm font-semibold text-white sm:text-base"
                          title={campaign.name || t("table.fallback.untitled")}
                        >
                          {campaign.name || t("table.fallback.untitled")}
                        </div>
                        <div className="mt-1 flex items-center space-x-1 text-xs text-gray-500 sm:text-sm">
                          <Icon
                            name="Clock"
                            className="h-3 w-3 flex-shrink-0"
                          />
                          <span>{createdTimeAgo}</span>
                        </div>
                      </td>

                      {/* Type */}
                      <td className="px-4 py-4 sm:px-6">
                        <div className="flex items-center space-x-2">
                          <Icon
                            name={contactTypeBadge.icon}
                            className={`h-4 w-4 flex-shrink-0 ${contactTypeBadge.text}`}
                          />
                          <span className="text-sm text-gray-300 capitalize sm:text-base">
                            {campaign.contact_type || t("table.fallback.email")}
                          </span>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-4 text-center sm:px-6">
                        <span
                          className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium whitespace-nowrap ${
                            campaign.status === "ended"
                              ? "border-green-500/40 bg-green-500/10 text-green-400"
                              : campaign.status === "scheduled"
                                ? "border-blue-500/40 bg-blue-500/10 text-blue-400"
                                : campaign.status === "draft"
                                  ? "border-gray-500/40 bg-gray-500/10 text-gray-400"
                                  : "border-yellow-500/40 bg-yellow-500/10 text-yellow-400"
                          }`}
                        >
                          {getStatusLabel(campaign.status)}
                        </span>
                      </td>

                      {/* Performance (Open Rate / CTR) */}
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center">
                          <div className="px-4 text-center">
                            <div className="font-semibold whitespace-nowrap text-white">
                              {showStats ? `${openRate}%` : "0.0%"}
                            </div>
                            <div className="text-xs whitespace-nowrap text-gray-500 uppercase">
                              {t("table.metrics.openRate")}
                            </div>
                          </div>
                          <div className="h-8 border-r border-gray-500"></div>
                          <div className="px-4 text-center">
                            <div className="font-semibold whitespace-nowrap text-white">
                              {showStats ? `${clickRate}%` : "0.0%"}
                            </div>
                            <div className="text-xs whitespace-nowrap text-gray-500 uppercase">
                              {t("table.metrics.ctr")}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="sticky bottom-0 z-10 mt-4 flex flex-col items-center justify-between gap-4 rounded-xl border border-gray-800 bg-gray-900 p-4 shadow-[0_-4px_12px_rgba(0,0,0,0.3)] sm:flex-row">
              <div className="text-sm text-gray-400">
                {t("pagination.showing", {
                  from: (currentPage - 1) * CAMPAIGNS_PER_PAGE + 1,
                  to: Math.min(
                    currentPage * CAMPAIGNS_PER_PAGE,
                    pagination?.total || 0,
                  ),
                  total: pagination?.total || 0,
                })}
              </div>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                maxVisiblePages={MAX_VISIBLE_PAGES}
                disabled={isLoading}
              />
            </div>
          )}
        </>
      ) : (
        <div className="rounded-xl border border-gray-800 bg-gray-900 p-12 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-purple-500/20">
            <Icon name="Megaphone" className="h-8 w-8 text-purple-400" />
          </div>
          <h3 className="mb-2 text-lg font-medium text-white">
            {t("empty.title")}
          </h3>
          <p className="mb-6 text-gray-400">
            {searchTerm
              ? t("empty.subtitleSearch")
              : t("empty.subtitleDefault")}
          </p>
          <button
            onClick={handleCreateCampaign}
            className="inline-flex items-center space-x-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
          >
            <Icon name="Plus" className="h-4 w-4" />
            <span>{t("actions.createCampaign")}</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default CampaignsPage;
